import React from 'react';

import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import { QuizContext } from '../../../../context/QuizContext';
import Explanation, { LoadingExplanation } from '../learn/Explanation';

const MultipleChoice = ({ user, quiz, question, explain, saveQuiz, courseId, subsectionId }) => {
    const { answer, setAnswer } = React.useContext(QuizContext);
    const { explanations, setExplanations } = React.useContext(QuizContext);
    const { loading, setLoading } = React.useContext(QuizContext);
    const { fetched, setFetched } = React.useContext(QuizContext);

    React.useEffect(() => {
        if (!explanations) setExplanations(Array(question.answers.length).fill({ explanation: null, show: false }));
        if (!loading) setLoading(Array(question.answers.length).fill(false));
    });

    React.useEffect(() => {
        if (user.courses.find((c) => c.id === courseId)
            ?.quiz?.find((q) => q.id === subsectionId)
            ?.questions?.find((q) => q.id === question.id)
            ?.answers?.length === 0 && quiz.type !== 'exam'
        ) {
            setExplanations(Array(question.answers.length).fill({ explanation: null, show: false }));
            fetch();
        }
    }, [user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)]);

    React.useEffect(() => {
        if (fetched) return;
        if (answer) return;

        fetch();
    }, [answer]);

    const fetch = async () => {
        const course = user.courses.find((c) => c.id === courseId);
        const qz = course?.quiz?.find((q) => q.id === subsectionId);
        const questionData = qz?.questions?.find((q) => q.id === question.id);

        const userAnswers = questionData?.answers || [];

        const savedAnswers = question.answers
            .filter((a) => userAnswers.some((ua) => ua.id === a.id))
            .map((a) => a.text);

        setAnswer(savedAnswers);
        setFetched(true);

        fetchExplanations(userAnswers, questionData);
    };

    const fetchExplanations = async (userAnswers, questionData) => {
        const explanationsArray = await Promise.all(
            question.answers.map(async (a, index) => {
                if (quiz.type !== 'exam') {
                    const isIncorrectUserAnswer =
                        userAnswers.some((ua) => ua.id === a.id) && !a.is_correct;

                    if (isIncorrectUserAnswer) {
                        setLoading((prevLoading) => {
                            const newLoading = [...prevLoading];
                            newLoading[index] = true;
                            return newLoading;
                        });

                        const existingExplanation = questionData?.answers
                            ?.find((ua) => ua.id === a.id && ua.explanation)?.explanation;

                        const explanation = existingExplanation || await explain(question, a.text);

                        setLoading((prevLoading) => {
                            const newLoading = [...prevLoading];
                            newLoading[index] = false;
                            return newLoading;
                        });

                        return { explanation, show: true };
                    }
                    return { explanation: null, show: false };
                } else {
                    return { explanation: null, show: false };
                }
            })
        );

        setExplanations(explanationsArray);
    }

    const handleChange = async (event) => {
        const value = event.target.value;
        const index = question.answers.findIndex((a) => a.text === value);

        const currentExplanations = explanations.length === question.answers.length
            ? explanations
            : question.answers.map((_, i) => explanations[i] || { explanation: null, show: false });

        const updatedAnswer = event.target.checked
            ? [...(answer || []), value]
            : (answer || []).filter((a) => a !== value);

        setAnswer(updatedAnswer);

        let explanationToUse = currentExplanations[index]?.explanation;
        if (quiz.type !== 'exam') {
            if (event.target.checked && !question.answers[index].is_correct) {
                setLoading((prevLoading) => {
                    const newLoading = [...prevLoading];
                    newLoading[index] = true;
                    return newLoading;
                });

                if (!explanationToUse) {
                    explanationToUse = await explain(question, value);
                }

                setLoading((prevLoading) => {
                    const newLoading = [...prevLoading];
                    newLoading[index] = false;
                    return newLoading;
                });
            }
        }

        const updatedExplanations = currentExplanations.map((exp, i) => {
            if (i === index && event.target.checked && !question.answers[index].is_correct) {
                return { explanation: explanationToUse, show: true };
            } else if (i === index) {
                return { explanation: currentExplanations[i]?.explanation, show: false };
            } else {
                return exp;
            }
        });
        setExplanations(updatedExplanations);

        const ids = question.answers
            .filter((a) => updatedAnswer.includes(a.text))
            .map((a) => a.id);
        saveQuiz(question, ids, updatedExplanations);
    };


    return (
        <FormGroup>
            {question.answers.map((a, index) => (
                <div key={`${a.id}_${index}`}>
                    <FormControlLabel
                        value={a.text}
                        label={a.text}
                        checked={answer?.includes(a.text) || false}
                        control={
                            <Checkbox
                                size='small'
                                onChange={handleChange}
                                disabled={quiz.type !== 'exam' && loading?.[index] || user.role !== 'student'}
                                sx={{
                                    color: 'primary.main',
                                    '&.Mui-checked': {
                                        color: a.is_correct || quiz.type === 'exam' ? 'primary.main' : 'error.main'
                                    },
                                    '&:hover': {
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            />
                        }
                        sx={{ '& .MuiTypography-root': { fontSize: '0.975rem' } }}
                    />
                    {(explanations[index]?.show && !loading?.[index] && quiz.type !== 'exam') && (
                        <Explanation explanation={explanations[index]?.explanation} mb={index === question.answers.length - 1} />
                    )}
                    {(!a.is_correct && answer?.includes(a.text) && loading?.[index] && quiz.type !== 'exam') && <LoadingExplanation />}
                </div>
            ))}
        </FormGroup>
    );
}

export default MultipleChoice;